import React, { useState } from "react";

// MaterialUI
import { Tooltip } from "@mui/material";

// FontAwesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";

// Styles
import { toDate } from "utils/general.utils";
import CommentsContainer from "./Comments.styles";

// Components
import CommentHistoryModal from "./CommentHistoryModal/CommentHistoryModal";

type Props = { comments: UserComment[]; sessionDate?: string };

const getHighlightClass = (commentDate: string, sessionDate?: string) => {
    if (!sessionDate) return "";
    const commentTime = new Date(commentDate).getTime();
    const sessionTime = new Date(sessionDate).getTime();
    const tenDaysMs = 10 * 24 * 60 * 60 * 1000;
    if (commentTime > sessionTime + tenDaysMs) return "latest-update";
    if (commentTime >= sessionTime && commentTime <= sessionTime + tenDaysMs)
        return "during-session";
    if (commentTime < sessionTime) return "legacy";
    return "";
};

const commentCell = (
    comment: UserComment,
    sessionDate?: string,
    onHistoryClick?: (commentId: string) => void
) => {
    return (
        <div className={"comment"} key={comment.id}>
            <div data-cy="comment-name" className={"name"}>
                <div>
                    {`${comment.analyst_name}`}{" "}
                    <span>{`${toDate(comment.created)}`}</span>{" "}
                    {comment.parent_comment_id && (
                        <Tooltip title="View comment history">
                            <FontAwesomeIcon
                                className="history-icon"
                                icon={faClock}
                                onClick={() => onHistoryClick?.(comment.id)}
                            />
                        </Tooltip>
                    )}
                </div>
            </div>
            <div
                data-cy="comment-answer"
                className={`answer ${getHighlightClass(
                    comment.created,
                    sessionDate
                )}`}
            >
                {comment.text}
            </div>
        </div>
    );
};

export default function Comments({ comments, sessionDate }: Props) {
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedCommentId, setSelectedCommentId] = useState("");

    const handleHistoryClick = (commentId: string) => {
        setSelectedCommentId(commentId);
        setShowHistoryModal(true);
    };

    const handleCloseHistoryModal = () => {
        setShowHistoryModal(false);
        setSelectedCommentId("");
    };

    return (
        <>
            <CommentsContainer className={"comments"}>
                {comments?.length > 0 ? (
                    comments
                        .slice()
                        .sort(
                            (a: UserComment, b: UserComment) =>
                                new Date(b.created).getTime() -
                                new Date(a.created).getTime()
                        )
                        .map((comment: UserComment) =>
                            commentCell(
                                comment,
                                sessionDate,
                                handleHistoryClick
                            )
                        )
                ) : (
                    <div className={"noComments"}>
                        <p>NO COMMENTS</p>
                    </div>
                )}
            </CommentsContainer>

            {showHistoryModal && (
                <CommentHistoryModal
                    commentId={selectedCommentId}
                    setShowModal={handleCloseHistoryModal}
                />
            )}
        </>
    );
}
